// src/bundles/Categories/components/CategoriesApp.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import HierarchicalTreeSelector from '../../Common/components/HierarchicalTreeSelector';

// Helper function for consistent image URL handling
const getImageUrl = (imagePath) => {
  if (!imagePath) return '/assets/placeholder.jpg';
  
  // Check if the image path is a full URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Check if it already has /assets/ prefix
  if (imagePath.startsWith('/assets/')) {
    return imagePath;
  }
  
  // Otherwise, prefix with /assets/
  return `/assets/${imagePath}`;
};

// PRODUCT CARD
const ProductCard = ({ product }) => {
  const sub = product.subcategory;
  const subId = sub?.id;
  // link directly to the model in the subcategory page
  const href = subId ? `/subcategories/${subId}/model/${product.id}` : '#';

  // Use first product image if available, fallback to subcategory or category image
  const imgUrl = (product.image_urls && product.image_urls.length > 0) 
    ? product.image_urls[0] 
    : sub?.image_url || product.category.image_url;

  return (
    <a
      href={href}
      className="block bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
    >
      {imgUrl && (
        <img
          src={imgUrl}
          alt={product.name}
          className="w-full h-32 object-cover mb-2 rounded"
        />
      )}
      <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
      <p className="text-gray-600 mb-1">
        {product.category.name}
        {sub?.name ? ` > ${sub.name}` : ''}
      </p>
    </a>
  );
};

// SUBCATEGORY CARD
const SubcategoryCard = ({ subcategory, onSelect }) => {
  return (
    <div 
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => onSelect(subcategory)}
    >
      {subcategory.image_url && (
        <img
          src={subcategory.image_url}
          alt={subcategory.name}
          className="w-full h-32 object-cover mb-2 rounded"
        />
      )}
      <h3 className="text-lg font-semibold mb-1">{subcategory.name}</h3>
      {subcategory.description && (
        <p className="text-gray-600 mb-2">{subcategory.description}</p>
      )}
    </div>
  );
};

// CATEGORY CARD
const CategoryCard = ({ cat, onSelect }) => {
  return (
    <div 
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => onSelect(cat)}
    >
      {cat.image_url && (
        <img
          src={cat.image_url}
          alt={cat.name}
          className="w-full h-32 object-cover mb-2 rounded"
        />
      )}
      <h3 className="text-lg font-semibold mb-1">{cat.name}</h3>
      {cat.description && (
        <p className="text-gray-600 mb-2">{cat.description}</p>
      )}
    </div>
  );
};

// NO RESULTS
const NoResults = ({ onClear }) => (
  <div className="text-center py-10">
    <p className="text-gray-500">No products match those filters.</p>
    <button
      className="mt-4 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
      onClick={onClear}
    >
      Clear Filters
    </button>
  </div>
);

// MAIN APP
const CategoriesApp = ({
  categories = [],
  virtual_products = [],
  selected_category = null,
  subcategories = [],
  filter_values = {}
}) => {
  const [selectedTreeItem, setSelectedTreeItem] = useState(null);
  const [displayContent, setDisplayContent] = useState([]);
  const [displayMode, setDisplayMode] = useState('categories');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Log virtual products for debugging
  useEffect(() => {
    console.log('Virtual Products:', virtual_products);
    if (virtual_products.length > 0) {
      console.log('Sample product filter_values:', virtual_products[0]);
      console.log('All unique filter_values:', virtual_products.map(vp => ({
        name: vp.name,
        filter_values: vp.filter_values
      })));
    }
  }, [virtual_products]);

  // Handle tree selection changes
  const handleTreeSelection = (selection) => {
    setSelectedTreeItem(selection);
    
    // Update URL to reflect selection
    const url = new URL(window.location);
    url.search = ''; // Clear existing params
    
    if (!selection) {
      // No selection - show categories
      setDisplayContent(categories);
      setDisplayMode('categories');
    } else if (selection.type === 'category') {
      // Category selected - show its subcategories
      url.searchParams.set('category', selection.id);
      const category = selection.item;
      if (category.subcategories && category.subcategories.length > 0) {
        setDisplayContent(category.subcategories);
        setDisplayMode('subcategories');
      } else {
        // Category has no subcategories, show products
        const categoryProducts = virtual_products.filter(p => p.category.id === category.id);
        setDisplayContent(categoryProducts);
        setDisplayMode('products');
      }
    } else if (selection.type === 'subcategory') {
      // Subcategory selected
      url.searchParams.set('subcategory', selection.id);
      if (selection.parentCategoryId) {
        url.searchParams.set('category', selection.parentCategoryId);
      }
      
      const subcategory = selection.item;
      if (subcategory.is_leaf) {
        // Leaf subcategory - redirect to subcategory details page
        window.location.href = `/subcategories/${subcategory.id}`;
        return; // Exit early since we're redirecting
      } else {
        // Non-leaf subcategory - show child subcategories
        setDisplayContent(subcategory.children || []);
        setDisplayMode('subcategories');
        // Reset filters for non-leaf subcategories
        setFilteredProducts([]);
        setHasActiveFilters(false);
      }
    }
    
    // Update URL without page refresh
    window.history.pushState(null, '', url.toString());
  };

  // Handle card clicks - find the parent category for subcategories (recursive)
  const findParentCategoryId = (subcategoryId) => {
    const findInCategory = (category) => {
      const findInSubs = (subs) => {
        for (const sub of subs) {
          if (sub.id === subcategoryId) return category.id;
          if (sub.children && sub.children.length > 0) {
            const found = findInSubs(sub.children);
            if (found) return found;
          }
        }
        return null;
      };
      
      return category.subcategories ? findInSubs(category.subcategories) : null;
    };
    
    // Search through all categories
    for (const category of categories) {
      const found = findInCategory(category);
      if (found) return found;
    }
    return null;
  };

  const handleCategoryCardClick = (category) => {
    const selection = { id: category.id, type: 'category', item: category };
    handleTreeSelection(selection);
  };

  const handleSubcategoryCardClick = (subcategory) => {
    // If it's a leaf subcategory, redirect directly to the details page
    if (subcategory.is_leaf) {
      window.location.href = `/subcategories/${subcategory.id}`;
      return;
    }
    
    // Otherwise, handle normal navigation for non-leaf subcategories
    const parentCategoryId = findParentCategoryId(subcategory.id);
    const selection = { 
      id: subcategory.id, 
      type: 'subcategory', 
      item: subcategory, 
      parentCategoryId 
    };
    handleTreeSelection(selection);
  };

  // Read URL parameters and set initial selection
  useEffect(() => {
    if (categories.length === 0 || virtual_products.length === 0) return; // Wait for data to load
    
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    const subcategoryParam = urlParams.get('subcategory');
    
    if (subcategoryParam) {
      // Find the subcategory in the hierarchy (recursive search)
      const findSubcategoryInHierarchy = (subcategoryId) => {
        const findInCategory = (category) => {
          const findInSubs = (subs, parentCategory) => {
            for (const sub of subs) {
              if (sub.id === parseInt(subcategoryId)) {
                return { subcategory: sub, category: parentCategory };
              }
              if (sub.children && sub.children.length > 0) {
                const found = findInSubs(sub.children, parentCategory);
                if (found) return found;
              }
            }
            return null;
          };
          
          return category.subcategories ? findInSubs(category.subcategories, category) : null;
        };
        
        // Search through all categories
        for (const category of categories) {
          const found = findInCategory(category);
          if (found) return found;
        }
        return null;
      };
      
      // Find the subcategory (now searches all categories recursively)
      const found = findSubcategoryInHierarchy(subcategoryParam);
      
      if (found) {
        const selection = {
          id: found.subcategory.id,
          type: 'subcategory',
          item: found.subcategory,
          parentCategoryId: found.category.id
        };
        setSelectedTreeItem(selection);
        
        // Set display content directly without calling handleTreeSelection to avoid URL update
        const subcategory = found.subcategory;
        if (subcategory.is_leaf) {
          // Redirect to subcategory details page for leaf subcategories
          window.location.href = `/subcategories/${subcategory.id}`;
          return;
        } else {
          setDisplayContent(subcategory.children || []);
          setDisplayMode('subcategories');
        }
        return;
      }
    }
    
    if (categoryParam) {
      const category = categories.find(cat => cat.id === parseInt(categoryParam));
      if (category) {
        const selection = {
          id: category.id,
          type: 'category',
          item: category
        };
        setSelectedTreeItem(selection);
        
        // Set display content directly
        if (category.subcategories && category.subcategories.length > 0) {
          setDisplayContent(category.subcategories);
          setDisplayMode('subcategories');
        } else {
          const categoryProducts = virtual_products.filter(p => p.category.id === category.id);
          setDisplayContent(categoryProducts);
          setDisplayMode('products');
        }
        return;
      }
    }
    
    // Default: show categories if no selection
    if (!selectedTreeItem) {
      setDisplayContent(categories);
      setDisplayMode('categories');
    }
  }, [categories, virtual_products]); // Depend on both categories and virtual_products

  // Handle filter changes for product display
  const handleFilterChange = (filtered) => {
    console.log('CategoriesApp: Received filtered products:', filtered.length, 'Display content:', displayContent.length);
    setFilteredProducts(filtered);
    setHasActiveFilters(filtered.length !== displayContent.length);
  };

  // Get products to display (filtered or original)
  const productsToDisplay = hasActiveFilters ? filteredProducts : displayContent;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex md:flex-row flex-col gap-8">
        {/* Left Sidebar - Tree Navigation with integrated filters */}
        <div className="md:w-1/4 w-full">
          <HierarchicalTreeSelector
            categories={categories}
            selectedItem={selectedTreeItem}
            onSelectionChange={handleTreeSelection}
            products={displayMode === 'products' ? displayContent : []}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Main Content */}
        <div className="md:w-3/4 w-full">
          {/* Content Summary */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {displayMode === 'categories' && `Exibindo ${displayContent.length} categorias`}
              {displayMode === 'subcategories' && `Exibindo ${displayContent.length} subcategorias`}
              {displayMode === 'products' && `Exibindo ${productsToDisplay.length} produtos${hasActiveFilters ? ` (${displayContent.length} total)` : ''}`}
            </p>
          </div>
          
          {displayMode === 'categories' ? (
            // Show categories
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayContent.map(cat => (
                <CategoryCard key={cat.id} cat={cat} onSelect={handleCategoryCardClick} />
              ))}
            </div>
          ) : displayMode === 'subcategories' ? (
            // Show subcategories
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayContent.map(sub => (
                <SubcategoryCard key={sub.id} subcategory={sub} onSelect={handleSubcategoryCardClick} />
              ))}
            </div>
          ) : displayMode === 'products' && productsToDisplay.length > 0 ? (
            // Show products
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productsToDisplay.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          ) : (
            // No results
            <div className="text-center py-10">
              <p className="text-gray-500">Nenhum item encontrado.</p>
              <button
                className="mt-4 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
                onClick={() => handleTreeSelection(null)}
              >
                Limpar Seleção
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CategoriesApp.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image_url: PropTypes.string,
      description: PropTypes.string,
      manufacturer: PropTypes.string
    })
  ),
  virtual_products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        manufacturer: PropTypes.string,
        image_url: PropTypes.string
      }).isRequired,
      subcategory: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        image_url: PropTypes.string,
        parent_subcategory_id: PropTypes.number,
        parent_name: PropTypes.string
      }),
      specifications: PropTypes.object,
      filter_values: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  selected_category: PropTypes.number,
  subcategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image_url: PropTypes.string,
      description: PropTypes.string,
      manufacturer: PropTypes.string
    })
  ),
  filter_values: PropTypes.object
};

CategoriesApp.defaultProps = {
  categories: [],
  virtual_products: [],
  selected_category: null,
  subcategories: [],
  filter_values: {}
};

export default CategoriesApp;
